import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { outboxQueryOptions, providerBalanceQueryOptions } from '@/api/notifications-api'
import type { NotificationStatus } from '@/api/notifications-types'
import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { NotificationRow, ProviderBalancePanel } from '@/features/notifications/outbox-panels'

/**
 * Every message the institution has queued, and what became of it.
 *
 * The filter defaults to everything rather than to failures. The question this screen is usually
 * opened to answer is "was my customer told?", and a view that showed only failures would answer it
 * with an empty list whether the message was delivered or never queued at all.
 */
const FILTERS: { label: string; status?: NotificationStatus }[] = [
  { label: 'Everything' },
  { label: 'Waiting', status: 'Pending' },
  { label: 'Delivered', status: 'Delivered' },
  { label: 'Failed', status: 'Failed' },
  { label: 'Given up', status: 'DeadLettered' },
  { label: 'Not sent on purpose', status: 'Suppressed' },
]

export function NotificationOutboxPage() {
  const [status, setStatus] = useState<NotificationStatus | undefined>(undefined)

  const outbox = useQuery(outboxQueryOptions({ status, pageSize: 25 }))
  const balance = useQuery(providerBalanceQueryOptions())

  return (
    <>
      <PageHeader
        title="Messages"
        description="What the institution has sent, what it is still trying to send, and what it decided not to."
      />

      <ProviderBalancePanel balance={balance.data} />

      <div className="my-4 flex flex-wrap gap-2">
        {FILTERS.map((filter) => (
          <button
            key={filter.label}
            type="button"
            onClick={() => setStatus(filter.status)}
            className={
              status === filter.status
                ? 'rounded-md bg-brand px-3 py-2 text-sm font-medium text-white'
                : 'rounded-md border border-border px-3 py-2 text-sm text-body hover:bg-brand-tint'
            }
          >
            {filter.label}
          </button>
        ))}
      </div>

      {outbox.isPending ? <Skeleton className="h-64 w-full" /> : null}

      {outbox.isError ? (
        <ErrorSurface
          title="The outbox could not be read"
          error={outbox.error}
          onRetry={() => void outbox.refetch()}
        />
      ) : null}

      {outbox.data && outbox.data.items.length === 0 ? (
        <p className="rounded-lg border border-border bg-surface p-6 text-sm text-body">
          Nothing here. Either nothing has been queued, or nothing matches this filter — the two look
          the same from a list, so try Everything before concluding the first.
        </p>
      ) : null}

      <div className="space-y-3">
        {outbox.data?.items.map((notification) => (
          <NotificationRow key={notification.id} notification={notification} />
        ))}
      </div>

      {outbox.data && outbox.data.totalCount > outbox.data.items.length ? (
        <p className="mt-4 text-sm text-muted">
          Showing {outbox.data.items.length} of {outbox.data.totalCount}.
        </p>
      ) : null}
    </>
  )
}
